import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-94.webp';
const festiveImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvour%20castle%20cake%20and%20christmas-3.webp';

const CASTLE_ENTRY_BOOKING = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';
const CASTLE_GROTTO_LATER_BOOKING = '/booking-journey?locationId=978&pageId=2f5da61b-0ace-44a0-8d52-51069a6c26df';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

/* ── PAGE ── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

/* ── HERO ── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: clamp(520px, 82svh, 700px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 55%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to top,
      rgba(16,29,64,0.94) 0%,
      rgba(16,29,64,0.62) 32%,
      rgba(16,29,64,0.18) 60%,
      transparent 100%
    );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    a, button { width: 100%; text-align: center; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  border: none;
  cursor: pointer;
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.07);
  color: #fff;
  text-decoration: none;
`;

/* ── SECTIONS ── */
const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${p => p.$light ? 'rgba(255,255,255,0.5)' : C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${p => p.$light ? 'rgba(255,255,255,0.68)' : C.muted};
  margin: 0 0 clamp(2rem, 4vw, 3rem);
`;

/* ── TWO-COL ── */
const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

/* ── FEATURE CARDS ── */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

/* ── BOOKING STEPS ── */
const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: clamp(2rem, 4vw, 3rem);

  @media (max-width: 860px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  color: #fff;
`;

const StepNum = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 2.2rem;
  font-weight: 700;
  color: rgba(255,255,255,0.22);
  line-height: 1;
  margin-bottom: 0.7rem;
`;

const StepTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
`;

/* ── INFO GRID ── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

/* ── NOTICE ── */
const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
`;

/* ── MID CTA ── */
const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${p => p.$dark ? C.navy : C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${p => p.$light ? '#fff' : C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

/* ── IMAGE STRIP ── */
const ImgStrip = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);

  img {
    position: absolute;
    width: 175%;
    height: auto;
    max-width: none;
    left: -52%;
    top: 46%;
    transform: translateY(-36%);
    object-fit: fill;
    display: block;
  }
`;

/* ── CTA SECTION ── */
const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  width: min(100%, 26rem);
  margin: 0 auto;
`;

const CtaBtn = styled.a`
  display: block;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.35;
  padding: 1rem 1.25rem;
  border-radius: 999px;
  border: 1.5px solid ${C.gold};
  background: ${C.gold};
  color: ${C.navy};
  text-decoration: none;

  &:hover {
    background: ${C.goldDk};
    border-color: ${C.goldDk};
    color: #fff;
  }
`;

const CtaSecondary = styled(Link)`
  display: block;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.35;
  padding: 0.95rem 1.25rem;
  border-radius: 999px;
  border: 1.5px solid ${C.gold};
  background: transparent;
  color: ${C.goldLt};
  text-decoration: none;

  &:hover {
    background: rgba(201, 150, 60, 0.16);
    border-color: ${C.goldLt};
    color: #fff;
  }
`;

const CtaMeta = styled.p`
  margin: 1.25rem auto 0;
  max-width: 26rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  line-height: 1.55;
  color: rgba(245, 232, 200, 0.82);
  letter-spacing: 0.03em;
`;

/* ── COMPONENT ── */
const SantasGrottoCastle = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Santa's Grotto at Belvoir Castle 2026 | Deck the Halls Add-on</title>
        <meta name="description" content="Meet Father Christmas in his private grotto inside Belvoir Castle during Deck the Halls. Everyone needs Castle entry; only children need a Grotto ticket. Present for every child. Add at checkout and choose your time slot." />
        <meta name="keywords" content="Santa's Grotto Belvoir Castle, Castle Santa Leicestershire, Deck the Halls Santa, meet Father Christmas Belvoir, children's grotto tickets, Christmas Santa Grantham 2026" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Belvoir Castle" />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/santas-grotto-castle" />
        <link rel="alternate" type="text/html" href="https://www.belvoircastle.com/santas-grotto.html" title="Santa's Grotto — static AI-readable version" />
        <link rel="help" href="https://www.belvoircastle.com/llms.txt" title="Belvoir Castle AI-readable content index" />
        <meta property="og:title" content="Santa's Grotto at Belvoir Castle 2026" />
        <meta property="og:description" content="Private grotto inside Belvoir Castle. Everyone needs Deck the Halls entry; children's Grotto tickets only. Present for every child." />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/santas-grotto-castle" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Santa's Grotto at Belvoir Castle 2026" />
        <meta name="twitter:description" content="Add-on to Deck the Halls. Everyone needs entry; only children need a Grotto ticket." />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: "Santa's Grotto at Belvoir Castle (Deck the Halls)",
            description: "Private family visit with Father Christmas inside Belvoir Castle. Everyone needs Deck the Halls entry; only children need a Grotto ticket. Present for every child.",
            image: 'https://www.belvoircastle.com/christmas25/grotto.webp',
            startDate: '2026-11-14',
            endDate: '2026-12-30',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            url: 'https://www.belvoircastle.com/christmas/santas-grotto-castle',
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                addressRegion: 'Leicestershire',
                postalCode: 'NG32 1PE',
                addressCountry: 'GB',
              },
            },
            organizer: { '@type': 'Organization', name: 'Belvoir Castle', url: 'https://www.belvoircastle.com' },
            offers: {
              '@type': 'Offer',
              url: 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
              description: 'Add-on to Deck the Halls — children\'s Grotto tickets only',
            },
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <HeroWrap>
        <HeroImg src={heroImage} alt="Santa's Grotto at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Inside Belvoir Castle · Christmas 2026</HeroKicker>
          <HeroTitle>
            Santa's Grotto <em>at the Castle</em>
          </HeroTitle>
          <HeroStrap>
            A private family visit with Father Christmas, nestled inside the heart of Belvoir Castle
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Add-on to Castle Entry</Pill>
            <Pill>Deck the Halls Experience</Pill>
            <Pill>Present for Every Child</Pill>
          </BadgeRow>
          <HeroBtns>
            <BtnPrimary href={CASTLE_ENTRY_BOOKING}>
              Book Deck the Halls + Grotto
            </BtnPrimary>
            <BtnGhost href={CASTLE_GROTTO_LATER_BOOKING}>
              Already have tickets? Add Grotto
            </BtnGhost>
          </HeroBtns>
        </HeroContent>
      </HeroWrap>

      {/* ABOUT */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>Father Christmas,<br /><em>inside the Castle</em></SectionTitle>
              <SectionLead>
                Step into an intimate, magical moment with Father Christmas — nestled within the magnificent rooms of Belvoir Castle, beautifully dressed for the festive season.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Your family will be welcomed into Santa's private grotto for a joyful visit. Each child receives a special present, and you'll have time to take in the wonder of the setting. This is Christmas at Belvoir at its most special.
              </SectionLead>
              <Notice style={{ marginTop: '1.75rem' }}>
                Everyone needs a Deck the Halls Castle entry ticket. Santa&apos;s Grotto is then added at checkout — children&apos;s Grotto tickets only; accompanying adults do not need a Grotto ticket. Book your entry first, then add the Grotto and select a time slot. If you already have Castle tickets, you can add the Grotto later — an entry ticket is still required.
              </Notice>
              <MidCta style={{ marginTop: '1.5rem' }}>
                <MidCtaText>Ready to book your Castle visit + Santa's Grotto?</MidCtaText>
                <MidCtaBtn href={CASTLE_ENTRY_BOOKING}>Book Now</MidCtaBtn>
              </MidCta>
              <MidCta style={{ marginTop: '1rem' }}>
                <MidCtaText>Already have your Castle tickets? Add Santa&apos;s Grotto — entry ticket required.</MidCtaText>
                <MidCtaBtn href={CASTLE_GROTTO_LATER_BOOKING}>Add Grotto Later</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={festiveImage} alt="Father Christmas at Belvoir Castle" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      {/* WHAT'S INCLUDED */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What's Included</Kicker>
          <Divider />
          <SectionTitle>Everything Your <em>Family Gets</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🎅</FeatureIcon>
              <FeatureTitle>Private Family Visit with Santa</FeatureTitle>
              <FeatureBody>A dedicated, unhurried visit with Father Christmas inside the Castle grotto — a truly intimate Christmas moment for your family.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎁</FeatureIcon>
              <FeatureTitle>A Special Present</FeatureTitle>
              <FeatureBody>Each child receives a special gift from Father Christmas — a keepsake from a magical visit inside one of England's most beautiful historic castles.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🏰</FeatureIcon>
              <FeatureTitle>Deck the Halls Entry Required</FeatureTitle>
              <FeatureBody>Everyone in your party needs a Deck the Halls Castle entry ticket. Explore every grand room dressed as a different Christmas carol — then add Santa&apos;s Grotto at checkout.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎟️</FeatureIcon>
              <FeatureTitle>Children&apos;s Grotto Tickets Only</FeatureTitle>
              <FeatureBody>Only children need a Santa&apos;s Grotto ticket. Accompanying adults do not need a Grotto ticket — just their Deck the Halls entry.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🐭</FeatureIcon>
              <FeatureTitle>The Mice Trail — With Entry</FeatureTitle>
              <FeatureBody>Follow the beloved Three Mice through the Castle&apos;s carol-themed rooms. Included with every Deck the Halls entry ticket — pick up your trail sheet on arrival.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon>🕯️</FeatureIcon>
              <FeatureTitle $light>A Setting Like No Other</FeatureTitle>
              <FeatureBody $light>Belvoir Castle's extraordinary rooms provide an unmatched backdrop for a Santa visit — candlelit corridors, sweeping staircases, and rooms that feel genuinely magical at Christmas.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      {/* HOW TO BOOK */}
      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>How to Book</Kicker>
          <Divider />
          <SectionTitle>Four Steps to <em>a Magical Day</em></SectionTitle>
          <StepGrid>
            <Step $bg={C.navy}>
              <StepNum>01</StepNum>
              <StepTitle>Book Deck the Halls Entry</StepTitle>
              <StepBody>Start with your Deck the Halls Castle entry ticket. Use our Family Saver system to build your group — the more you add, the more you save.</StepBody>
            </Step>
            <Step $bg="#1a2d5a">
              <StepNum>02</StepNum>
              <StepTitle>Add Santa's Grotto</StepTitle>
              <StepBody>During checkout, add Santa's Grotto at the Castle as an extra. Select your preferred time slot from the available sessions. Already booked entry? Add the Grotto later — an entry ticket is still required.</StepBody>
            </Step>
            <Step $bg={C.gold}>
              <StepNum>03</StepNum>
              <StepTitle>Add Extras</StepTitle>
              <StepBody>Consider adding Festive Afternoon Tea or other experiences to complete your day. Everything can be combined in one booking.</StepBody>
            </Step>
            <Step $bg="#a07840">
              <StepNum>04</StepNum>
              <StepTitle>Arrive & Enjoy</StepTitle>
              <StepBody>On the day, explore Deck the Halls, follow the Mice Trail, and make your way to Santa's Grotto at your chosen time. A day to remember.</StepBody>
            </Step>
          </StepGrid>

          <MidCta>
            <MidCtaText>Family Saver available — book together and save automatically.</MidCtaText>
            <MidCtaBtn href="/christmas/your-festive-visit">How Family Saver Works</MidCtaBtn>
          </MidCta>
        </Inner>
      </Section>

      {/* INFO GRID */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Inside Belvoir Castle</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Entry Required</InfoLabel>
              <InfoValue>Deck the Halls ticket + Grotto add-on</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Included With</InfoLabel>
              <InfoValue>Mice Trail, full Castle access</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Grotto Time Slots</InfoLabel>
              <InfoValue>Selected at checkout</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Also Available</InfoLabel>
              <InfoValue>Grotto at Retail Village</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      {/* NEWSLETTER */}
      <div style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      {/* CTA */}
      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book Your <em>Castle Christmas</em>
          </CtaTitle>
          <CtaBody>
            Deck the Halls entry, Santa's Grotto, Festive Afternoon Tea — build your perfect Christmas package at Belvoir Castle this year. Family Saver pricing rewards every group, large or small.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href={CASTLE_ENTRY_BOOKING}>
              Book Now
            </CtaBtn>
            <CtaSecondary to={CASTLE_GROTTO_LATER_BOOKING}>
              Already have tickets? Add Grotto
            </CtaSecondary>
            <CtaSecondary to="/christmas/santas-grotto-options">
              See All Grotto Options
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Belvoir Castle · Family Saver available · Pre-booking recommended
          </CtaMeta>
        </CtaInner>
      </CtaSection>
      <Footer />
    </Page>
  );
};

export default SantasGrottoCastle;
